import React, {useState, useEffect} from 'react';
import {
    IndianRupee,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Search,
    Mail,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Filter,
    Heart,
    CreditCard,
    FileText,
    Users,
    ArrowUpDown,
    MessageSquareText,
    Maximize,
    CalendarClock,
    ArrowDown,
    ArrowUp
} from 'lucide-react';

const DonationsPage = () => {
    // State for donations data
    const [donations, setDonations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalDonations, setTotalDonations] = useState(0);

    // Filter state
    const [emailFilter, setEmailFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');

    // Stats state
    const [stats, setStats] = useState({
        totalDonations: 0,
        totalAmount: 0,
        avgDonation: 0,
        uniqueDonors: 0
    });

    // Filter submission
    const [appliedEmailFilter, setAppliedEmailFilter] = useState('');
    const [appliedSortOrder, setAppliedSortOrder] = useState('newest');

    // Mobile filters visibility
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Fetch donations
    const fetchDonations = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            // Build query string
            let queryParams = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString(),
                sort: appliedSortOrder
            });

            if (appliedEmailFilter) {
                queryParams.append('email', appliedEmailFilter);
            }

            const response = await fetch(`https://api.digitalaalim.com/donations/admin?${queryParams.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch donations: ${response.statusText}`);
            }

            const data = await response.json();

            setDonations(data.items || []);
            setTotalDonations(data.total);

            // Calculate stats
            if (data.items && data.items.length > 0) {
                const totalAmount = data.items.reduce((sum, donation) => sum + donation.amount, 0);
                const uniqueDonorsSet = new Set(data.items.map(donation => donation.email));

                setStats({
                    totalDonations: data.total,
                    totalAmount: totalAmount,
                    avgDonation: totalAmount / data.items.length,
                    uniqueDonors: uniqueDonorsSet.size
                });
            }

        } catch (err) {
            console.error('Error fetching donations:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDonations();
    }, [page, pageSize, appliedEmailFilter, appliedSortOrder]);

    // Apply filters
    const applyFilters = () => {
        setPage(1); // Reset to first page
        setAppliedEmailFilter(emailFilter);
        setAppliedSortOrder(sortOrder);
    };

    // Reset filters
    const resetFilters = () => {
        setEmailFilter('');
        setSortOrder('newest');
        setAppliedEmailFilter('');
        setAppliedSortOrder('newest');
        setPage(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    // Format date
    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleString(undefined, options);
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalDonations / pageSize);

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            // Always show first page
            items.push(1);

            // Calculate range around current page
            let startPage = Math.max(2, page - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

            // Adjust if at boundaries
            if (startPage > 2) items.push('...');

            // Add pages in range
            for (let i = startPage; i <= endPage; i++) {
                items.push(i);
            }

            // Add ellipsis if needed
            if (endPage < totalPages - 1) items.push('...');

            // Always add last page
            items.push(totalPages);
        }

        return items;
    };

    // Stat Card Component
    const StatCard = ({icon, title, value, bgColor}) => (
        <div
            className={`${bgColor} rounded-2xl shadow-sm p-6 transition-all duration-200 hover:shadow-md flex flex-col h-full`}>
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-white bg-opacity-20">
                    {icon}
                </div>
            </div>
            <div className="mt-auto">
                <h3 className="text-xl font-bold text-white">{value}</h3>
                <p className="text-sm text-white text-opacity-90 font-medium mt-1">{title}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 space-y-8 p-4 md:p-8">
            {/* Header section */}
            <div
                className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 md:p-8 shadow-lg text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-pattern opacity-10"></div>

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
                                <Heart className="text-pink-300" size={32}/>
                                Donations Dashboard
                            </h1>
                            <p className="mt-2 text-purple-100 max-w-2xl">
                                View and manage all donations received through the platform
                            </p>
                        </div>

                        <button
                            onClick={() => fetchDonations()}
                            className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors duration-150 self-start"
                        >
                            <RefreshCw size={16} className="mr-2"/>
                            Refresh Data
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                <StatCard
                    icon={<Heart className="h-5 w-5 text-white"/>}
                    title="Total Donations"
                    value={stats.totalDonations.toLocaleString()}
                    bgColor="bg-gradient-to-br from-purple-500 to-purple-600"
                />
                <StatCard
                    icon={<IndianRupee className="h-5 w-5 text-white"/>}
                    title="Total Amount"
                    value={formatCurrency(stats.totalAmount)}
                    bgColor="bg-gradient-to-br from-emerald-500 to-teal-600"
                />
                <StatCard
                    icon={<CreditCard className="h-5 w-5 text-white"/>}
                    title="Average Donation"
                    value={formatCurrency(stats.avgDonation || 0)}
                    bgColor="bg-gradient-to-br from-amber-500 to-orange-600"
                />
                <StatCard
                    icon={<Users className="h-5 w-5 text-white"/>}
                    title="Unique Donors"
                    value={stats.uniqueDonors.toLocaleString()}
                    bgColor="bg-gradient-to-br from-blue-500 to-indigo-600"
                />
            </div>

            {/* Filters section */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-between">
                    <div className="w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <Filter size={18}/>
                                Filters
                            </h2>

                            <button
                                className="md:hidden inline-flex items-center text-sm text-gray-600"
                                onClick={() => setShowMobileFilters(!showMobileFilters)}
                            >
                                {showMobileFilters ? "Hide Filters" : "Show Filters"}
                                {showMobileFilters ? <ChevronLeft className="ml-1" size={16}/> :
                                    <ChevronRight className="ml-1" size={16}/>}
                            </button>
                        </div>

                        <div
                            className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${showMobileFilters ? 'block' : 'hidden md:grid'}`}>
                            <div className="flex flex-col gap-2">
                                <label htmlFor="emailFilter" className="text-sm font-medium text-gray-700">
                                    Email Filter
                                </label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail size={16} className="text-gray-400"/>
                                    </div>
                                    <input
                                        id="emailFilter"
                                        type="text"
                                        value={emailFilter}
                                        onChange={(e) => setEmailFilter(e.target.value)}
                                        placeholder="Filter by email..."
                                        className="pl-10 w-full border border-gray-300 rounded-lg py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-gray-50"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label htmlFor="sortOrder" className="text-sm font-medium text-gray-700">
                                    Sort Order
                                </label>
                                <div className="relative">
                                    <select
                                        id="sortOrder"
                                        value={sortOrder}
                                        onChange={(e) => setSortOrder(e.target.value)}
                                        className="w-full appearance-none border border-gray-300 rounded-lg py-2.5 pl-4 pr-10 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    >
                                        <option value="newest">Newest First</option>
                                        <option value="oldest">Oldest First</option>
                                    </select>
                                    <div
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <ArrowUpDown size={16} className="text-gray-500"/>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">
                                    Results Per Page
                                </label>
                                <select
                                    id="pageSize"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1); // Reset to first page when changing page size
                                    }}
                                    className="w-full appearance-none border border-gray-300 rounded-lg py-2.5 pl-4 pr-10 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 w-full md:w-auto mt-4 md:mt-0">
                        <button
                            onClick={resetFilters}
                            className="flex-1 md:flex-initial px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-150"
                        >
                            Reset
                        </button>
                        <button
                            onClick={applyFilters}
                            className="flex-1 md:flex-initial px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-150"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Donations List */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="flex flex-col items-center">
                        <Loader2 size={40} className="animate-spin text-purple-600 mb-4"/>
                        <p className="text-gray-500">Loading donations...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading donations</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {donations.length > 0 ? (
                        <div className="space-y-6">
                            {/* Donation cards */}
                            <div className="grid grid-cols-1 gap-4">
                                {donations.map((donation) => (
                                    <div
                                        key={donation.id}
                                        className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 transition-all duration-200 hover:shadow-md hover:border-purple-200"
                                    >
                                        <div className="p-5 md:p-6">
                                            <div
                                                className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                                                                <Heart className="h-6 w-6 text-white"/>
                                                            </div>
                                                            <div>
                                                                <h3 className="text-xl font-bold text-gray-900">
                                                                    {formatCurrency(donation.amount)}
                                                                </h3>
                                                                <div
                                                                    className="flex items-center mt-1 text-sm text-gray-500">
                                                                    <Mail className="h-4 w-4 mr-1 text-gray-400"/>
                                                                    <span
                                                                        className="truncate max-w-xs">{donation.email}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <div
                                                            className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
                                                            <div
                                                                className="flex items-center text-sm text-gray-500 mb-1">
                                                                <Calendar className="h-4 w-4 mr-1.5 text-gray-400"/>
                                                                Donation Date
                                                            </div>
                                                            <p className="text-sm font-medium text-gray-700">
                                                                {formatDate(donation.created_at)}
                                                            </p>
                                                        </div>

                                                        <div
                                                            className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-100">
                                                            <div
                                                                className="flex items-center text-sm text-gray-500 mb-1">
                                                                <FileText className="h-4 w-4 mr-1.5 text-gray-400"/>
                                                                Donation ID
                                                            </div>
                                                            <p className="text-sm font-medium text-gray-700 truncate"
                                                               title={donation.id}>
                                                                {donation.id}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {donation.note && (
                                                        <div className="mt-4">
                                                            <div
                                                                className="flex items-center text-sm text-gray-500 mb-1.5">
                                                                <MessageSquareText
                                                                    className="h-4 w-4 mr-1.5 text-gray-400"/>
                                                                Donation Note
                                                            </div>
                                                            <p className="text-sm text-gray-700 p-3 bg-purple-50 rounded-lg border border-purple-100">
                                                                {donation.note}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="text-sm text-gray-600">
                                        Showing <span className="font-medium">{donations.length}</span> of <span
                                        className="font-medium">{totalDonations}</span> donations
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="flex items-center space-x-1">
                                            <button
                                                onClick={() => handlePageChange(page - 1)}
                                                disabled={page === 1}
                                                className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                            >
                                                <span className="sr-only">Previous</span>
                                                <ChevronLeft className="h-5 w-5" aria-hidden="true"/>
                                            </button>

                                            <div className="hidden md:flex">
                                                {getPaginationItems().map((item, index) => (
                                                    item === '...' ? (
                                                        <span key={`ellipsis-${index}`}
                                                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                                                    ) : (
                                                        <button
                                                            key={`page-${item}`}
                                                            onClick={() => handlePageChange(item)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                                page === item
                                                                    ? 'z-10 bg-purple-600 border-purple-600 text-white'
                                                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                            } transition-colors duration-150`}
                                                        >
                                                            {item}
                                                        </button>
                                                    )
                                                ))}
                                            </div>

                                            <div
                                                className="md:hidden flex items-center px-4 py-2 text-sm font-medium text-gray-700">
                                                Page {page} of {totalPages}
                                            </div>

                                            <button
                                                onClick={() => handlePageChange(page + 1)}
                                                disabled={page === totalPages}
                                                className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                                            >
                                                <span className="sr-only">Next</span>
                                                <ChevronRight className="h-5 w-5" aria-hidden="true"/>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                            {appliedEmailFilter ? (
                                <div className="flex flex-col items-center">
                                    <Search size={60} className="text-gray-300 mb-4"/>
                                    <h3 className="text-xl font-medium text-gray-800 mb-2">No donations found</h3>
                                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                        No donations match your current search filters. Try adjusting your search
                                        criteria.
                                    </p>
                                    <button
                                        onClick={resetFilters}
                                        className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors duration-150"
                                    >
                                        Clear all filters
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <Heart size={60} className="text-gray-300 mb-4"/>
                                    <h3 className="text-xl font-medium text-gray-800 mb-2">No donations available</h3>
                                    <p className="text-gray-600 max-w-md mx-auto">
                                        There are no donations to display at this time.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DonationsPage;